export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const ALLOWED_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'];

const MAX_LONG_EDGE = 1920;
const JPEG_QUALITY = 0.85;
const KEEP_ORIGINAL_BYTES = 2 * 1024 * 1024;
const IMAGE_LOAD_TIMEOUT_MS = 15000;

const TYPE_EXTENSIONS = {
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
};

const getNameExtension = (name) => {
    const match = /\.([a-z0-9]+)$/i.exec(String(name || ''));
    return match ? match[1].toLowerCase() : '';
};

const loadWithImageElement = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    let timerId = null;
    const finish = (callback, arg) => {
        clearTimeout(timerId);
        URL.revokeObjectURL(url);
        callback(arg);
    };
    timerId = setTimeout(() => finish(reject, new Error('Image load timeout')), IMAGE_LOAD_TIMEOUT_MS);
    image.onload = () => finish(resolve, image);
    image.onerror = () => finish(reject, new Error('Image decode failed'));
    image.src = url;
});

const decodeImage = async (file) => {
    if (typeof createImageBitmap === 'function') {
        try {
            return await createImageBitmap(file, { imageOrientation: 'from-image' });
        } catch {
            // Старые Safari не принимают параметры, остальные случаи решит <img>
        }
    }
    return loadWithImageElement(file);
};

const canvasToBlob = (canvas) => new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY);
});

/**
 * Уменьшает фото до 1920 px по длинной стороне (JPEG ~0.85).
 * При любой ошибке возвращает исходный файл: загрузка важнее сжатия.
 */
export const compressImage = async (file) => {
    if (!file || !String(file.type || '').startsWith('image/')) return file;

    let source = null;
    let canvas = null;
    try {
        source = await decodeImage(file);
        const width = source.naturalWidth || source.width;
        const height = source.naturalHeight || source.height;
        if (!width || !height) return file;

        const scale = Math.min(1, MAX_LONG_EDGE / Math.max(width, height));
        const isSupported = Boolean(TYPE_EXTENSIONS[file.type]);
        if (scale === 1 && isSupported && file.size <= KEEP_ORIGINAL_BYTES) return file;

        canvas = document.createElement('canvas');
        canvas.width = Math.round(width * scale);
        canvas.height = Math.round(height * scale);
        const context = canvas.getContext('2d');
        if (!context) return file;
        context.drawImage(source, 0, 0, canvas.width, canvas.height);

        const blob = await canvasToBlob(canvas);
        if (!blob) return file;
        if (scale === 1 && isSupported && blob.size >= file.size) return file;

        const baseName = String(file.name || 'photo').replace(/\.[^.]+$/, '') || 'photo';
        return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
    } catch {
        return file;
    } finally {
        if (source && typeof source.close === 'function') source.close();
        if (canvas) {
            canvas.width = 0;
            canvas.height = 0;
        }
    }
};

/** Камеры и файловые менеджеры иногда отдают имя без расширения, а сервер проверяет его. */
export const ensureImageExtension = (file) => {
    if (getNameExtension(file.name)) return file;
    const extension = TYPE_EXTENSIONS[file.type];
    if (!extension) return file;
    return new File([file], `${file.name || 'photo'}.${extension}`, { type: file.type });
};

/** Возвращает текст ошибки или null, если файл можно отправлять. */
export const getImageValidationError = (file) => {
    if (!ALLOWED_IMAGE_EXTENSIONS.includes(getNameExtension(file.name))) {
        return 'Формат не поддерживается: нужны JPG, PNG или WEBP';
    }
    if (file.size > MAX_IMAGE_BYTES) {
        return 'Файл больше 10 МБ';
    }
    return null;
};
